import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import {
    Users,
    UserPlus,
    Shield,
    Trash2,
    CheckCircle2,
    XCircle,
    Info,
    ChevronLeft,
    Search,
    Settings2
} from 'lucide-react';
import NotificationToast from '../layout/NotificationToast';

const StaffManagement = () => {
    const { slug } = useParams();
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [email, setEmail] = useState('');
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [editingMember, setEditingMember] = useState(null);
    const [clientType, setClientType] = useState('events');

    const availablePermissions = [
        ...(clientType !== 'sports' ? [{ id: 'escanear_tickets', label: 'Escanear tickets', desc: 'Puede escanear QR de tickets en el evento' }] : []),
        { id: 'ver_estadisticas', label: 'Ver estadísticas de ventas', desc: 'Acceso a reportes y análisis de ingresos' },
        ...(clientType !== 'sports' ? [{ id: 'gestionar_tickets', label: 'Gestión de tickets', desc: 'Puede emitir tickets de cortesía e invitaciones' }] : []),
        { id: 'gestionar_staff', label: 'Gestión de Staff', desc: 'Puede crear, editar y eliminar miembros del staff' },
        ...(clientType !== 'sports'
            ? [{ id: 'gestionar_eventos', label: 'Gestión de Eventos', desc: 'Puede modificar información de eventos' }]
            : [{ id: 'gestionar_eventos', label: 'Gestión de Torneos', desc: 'Puede modificar información de torneos' }]),
        ...(clientType !== 'sports' ? [{ id: 'afiliado', label: 'Habilitar código de afiliado', desc: 'Permite al staff tener su propio link de venta' }] : [])
    ];

    useEffect(() => {
        const fetchClientData = async () => {
            try {
                const { data } = await axios.get(`/api/clients/${slug}`);
                setClientType(data.organizationType || 'events');
            } catch (err) {
                console.error('Error al cargar tipo de cliente', err);
            }
        };
        fetchClientData();
        fetchStaff();
    }, [slug]);

    const fetchStaff = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`/${slug}/api/users`);
            setStaff(data);
        } catch (err) {
            addNotification('Error al cargar el staff', 'error');
        } finally {
            setLoading(false);
        }
    };

    const addNotification = (message, type) => {
        setNotifications(prev => [...prev, { id: Date.now(), message, type }]);
    };

    const handleSaveStaff = async (e) => {
        e.preventDefault();
        try {
            if (editingMember) {
                await axios.put(`/${slug}/api/users/permissions/${editingMember._id}`, {
                    permissions: selectedPermissions
                });
                addNotification('Permisos actualizados correctamente', 'success');
            } else {
                await axios.post(`/${slug}/api/users/add`, { email, permissions: selectedPermissions });
                addNotification('Staff agregado correctamente', 'success');
            }
            closeModal();
            fetchStaff();
        } catch (err) {
            addNotification(err.response?.data?.message || 'Error al guardar staff', 'error');
        }
    };

    const openEditModal = (member) => {
        setEditingMember(member);
        setEmail(member.email);
        setSelectedPermissions(member.permissions || []);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingMember(null);
        setEmail('');
        setSelectedPermissions([]);
    };

    const handleRemoveStaff = async (userId) => {
        if (!window.confirm('¿Estás seguro de desvincular a este miembro del staff?')) return;
        try {
            await axios.delete(`/${slug}/api/users/${userId}`);
            addNotification('Staff desvinculado', 'success');
            fetchStaff();
        } catch (err) {
            addNotification('Error al eliminar staff', 'error');
        }
    };

    const togglePermission = (permId) => {
        setSelectedPermissions(prev =>
            prev.includes(permId) ? prev.filter(p => p !== permId) : [...prev, permId]
        );
    };

    if (loading) return <div className="flex justify-center py-20"><div className="w-10 h-10 border-4 border-primary-orange/20 border-t-primary-orange rounded-full animate-spin"></div></div>;

    return (
        <div className="container mx-auto px-6 py-12 max-w-6xl animate-fade-in-up">
            {notifications.map(n => (
                <NotificationToast key={n.id} message={n.message} type={n.type} onClose={() => setNotifications(prev => prev.filter(i => i.id !== n.id))} />
            ))}

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                <div>
                    <h1 className="text-4xl font-black text-white tracking-tight mb-2">Gestión de <span className="text-gradient">Staff</span></h1>
                    <p className="text-white/40 font-medium">Administra los miembros de tu equipo y sus permisos.</p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    className="flex items-center gap-2 px-6 py-3 bg-brand-gradient rounded-2xl text-xs font-black uppercase tracking-widest shadow-lg shadow-orange-500/20 hover:scale-105 transition-all"
                >
                    <UserPlus size={18} /> Nuevo Staff
                </button>
            </div>

            {/* Info Box */}
            <div className="bg-blue-500/5 border border-blue-500/10 p-6 rounded-[32px] mb-10 flex gap-4">
                <Info className="text-blue-400 shrink-0" size={24} />
                <div className="text-sm text-blue-100/60 leading-relaxed">
                    <p className="font-bold text-blue-300 mb-1">¿Cómo agregar miembros al equipo?</p>
                    <ol className="list-decimal ml-4 space-y-1">
                        <li>El miembro del equipo debe registrarse primero en la plataforma.</li>
                        <li>Una vez registrado, puedes agregarlo aquí usando su email.</li>
                        <li>Asigna los permisos necesarios según su rol.</li>
                    </ol>
                </div>
            </div>

            {/* Staff List */}
            <div className="bg-glass rounded-[40px] border-white/5 shadow-2xl overflow-hidden">
                <div className="px-8 py-6 border-b border-white/5 bg-white/[0.01]">
                    <h3 className="text-lg font-black text-white flex items-center gap-3">
                        <Users size={20} className="text-primary-orange" /> Miembros del Equipo
                    </h3>
                </div>

                {staff.length === 0 ? (
                    <div className="py-20 text-center">
                        <Users size={48} className="mx-auto text-white/10 mb-4" />
                        <p className="text-white/20 font-bold uppercase tracking-widest text-xs">No hay staff agregado aún</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-white/[0.02]">
                                    <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[3px]">Miembro</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[3px]">Rol</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[3px]">Permisos</th>
                                    <th className="px-8 py-5 text-right"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {staff.map((member) => (
                                    <tr key={member._id} className="group hover:bg-white/[0.01] transition-colors">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-white/40 font-black border border-white/5">
                                                    {member.name.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-black text-white leading-tight">{member.name}</p>
                                                    <p className="text-[10px] font-medium text-white/30">{member.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${member.role === 'admin' ? 'bg-primary-orange/10 text-primary-orange border-primary-orange/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                                }`}>
                                                {member.role}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="flex flex-wrap gap-1">
                                                {member.permissions.length > 0 ? member.permissions.map(p => (
                                                    <span key={p} className="px-2 py-0.5 bg-white/5 border border-white/10 rounded-md text-[9px] font-bold text-white/40">
                                                        {p.replace('_', ' ')}
                                                    </span>
                                                )) : <span className="text-[10px] text-white/10 italic">Sin permisos específicos</span>}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => openEditModal(member)}
                                                    className="p-2 text-white/20 hover:text-primary-orange hover:bg-primary-orange/10 rounded-xl transition-all"
                                                    title="Editar permisos"
                                                >
                                                    <Settings2 size={18} />
                                                </button>
                                                <button
                                                    onClick={() => handleRemoveStaff(member._id)}
                                                    className="p-2 text-white/20 hover:text-red-400 hover:bg-red-400/10 rounded-xl transition-all"
                                                    title="Eliminar staff"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center px-6">
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeModal}></div>
                    <div className="bg-[#121212] border border-white/10 w-full max-w-xl rounded-[40px] shadow-2xl relative overflow-hidden animate-fade-in-up">
                        <div className="p-8 border-b border-white/5">
                            <h2 className="text-2xl font-black text-white mb-1">
                                {editingMember ? 'Editar Permisos' : 'Agregar Nuevo Staff'}
                            </h2>
                            <p className="text-sm text-white/40">
                                {editingMember ? `Modificando acceso para ${editingMember.name}` : 'Ingresa el email de un usuario ya registrado.'}
                            </p>
                        </div>
                        <form onSubmit={handleSaveStaff} className="p-8 space-y-6">
                            <div>
                                <label className="block text-[10px] font-black text-white/30 uppercase tracking-[2px] mb-2">Email del Usuario</label>
                                <div className="relative">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={18} />
                                    <input
                                        type="email"
                                        required
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        disabled={!!editingMember}
                                        placeholder="ejemplo@email.com"
                                        className={`w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white focus:border-primary-orange/50 transition-colors outline-none ${editingMember ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black text-white/30 uppercase tracking-[2px] mb-4">Permisos Asignados</label>
                                <div className="grid grid-cols-1 gap-3">
                                    {availablePermissions.map(perm => (
                                        <div
                                            key={perm.id}
                                            onClick={() => togglePermission(perm.id)}
                                            className={`p-4 rounded-2xl border cursor-pointer transition-all flex items-center gap-4 ${selectedPermissions.includes(perm.id)
                                                ? 'bg-primary-orange/10 border-primary-orange/30'
                                                : 'bg-white/5 border-white/5 hover:border-white/20'
                                                }`}
                                        >
                                            <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all ${selectedPermissions.includes(perm.id)
                                                ? 'bg-primary-orange border-primary-orange'
                                                : 'border-white/20'
                                                }`}>
                                                {selectedPermissions.includes(perm.id) && <CheckCircle2 size={14} className="text-white" />}
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-white">{perm.label}</p>
                                                <p className="text-[10px] text-white/40">{perm.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex gap-4 pt-4">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="flex-1 py-4 bg-white/5 border border-white/10 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-white/10 transition-all"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-4 bg-brand-gradient rounded-2xl text-xs font-black uppercase tracking-widest shadow-lg shadow-orange-500/20 hover:scale-105 transition-all"
                                >
                                    {editingMember ? 'Actualizar Permisos' : 'Guardar Staff'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StaffManagement;
